import type { FeedbackDistributionBucket } from "@/lib/api";
import { FEEDBACK_CATEGORIES, feedbackCategoryLabel } from "@/lib/feedback";
import { cn } from "@/lib/utils";

// Horizontal bar chart of feedback grouped by type, with each bar split by
// rating bucket so the admin can read both "what kind of feedback is
// landing" and "how positive it is" in one glance. Pure CSS — no chart
// dependency since the dimensions are tiny and we don't need axes, tooltips,
// or animations beyond what flex+widths give us.
//
// Fed by the full-table `distribution` on the feedback page payload, so it
// stays a stable overview while the list below narrows with filters/search.

type RatingBucket = "0" | "1" | "2" | "3" | "4" | "5";

const RATING_ORDER: RatingBucket[] = ["1", "2", "3", "4", "5", "0"];

// J&J accent palette — reserved for data-viz surfaces like this chart.
const RATING_META: Record<RatingBucket, { label: string; bar: string }> = {
  "1": { label: "1 star", bar: "bg-jj-maroon-05" },
  "2": { label: "2 stars", bar: "bg-jj-orange-03" },
  "3": { label: "3 stars", bar: "bg-jj-yellow-01" },
  "4": { label: "4 stars", bar: "bg-jj-green-01" },
  "5": { label: "5 stars", bar: "bg-jj-green-03" },
  "0": { label: "No rating", bar: "bg-warm-gray-05" },
};

interface RowCount {
  category: string;
  total: number;
  byRating: Record<RatingBucket, number>;
}

export function FeedbackBarChart({ distribution }: { distribution: FeedbackDistributionBucket[] }) {
  const rows = categoryRows(distribution);
  const overall = overallRow(distribution);

  return (
    <div className="border-border bg-card rounded-md border p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <p className="text-foreground text-sm font-medium">Distribution</p>
        <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
          {overall.total} {overall.total === 1 ? "entry" : "entries"}
        </p>
      </div>

      {overall.total > 0 && (
        <div className="mb-3">
          <ChartRow
            label="Overall"
            row={overall}
            barClassName="h-7"
            labelClassName="text-foreground text-sm font-medium"
            totalClassName="text-foreground text-sm font-medium"
          />
        </div>
      )}

      {rows.length > 0 && (
        <>
          <p className="text-muted mb-2 text-[10px] font-medium tracking-[0.18em] uppercase">
            By type
          </p>
          <div className="space-y-2">
            {rows.map((row) => (
              <ChartRow key={row.category} label={feedbackCategoryLabel(row.category)} row={row} />
            ))}
          </div>
        </>
      )}

      <div className="border-border mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-3">
        {RATING_ORDER.map((bucket) => (
          <span key={bucket} className="text-muted inline-flex items-center gap-1.5 text-[11px]">
            <span className={cn("inline-block h-2.5 w-2.5 rounded-sm", RATING_META[bucket].bar)} />
            {RATING_META[bucket].label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ChartRow({
  label,
  row,
  barClassName,
  labelClassName,
  totalClassName,
}: {
  label: string;
  row: { total: number; byRating: Record<RatingBucket, number> };
  barClassName?: string;
  labelClassName?: string;
  totalClassName?: string;
}) {
  return (
    <div className="grid grid-cols-[6rem_1fr_2rem] items-center gap-3">
      <span className={cn("text-foreground truncate text-xs", labelClassName)}>{label}</span>
      <div className={cn("bg-warm-gray-02 relative h-5 overflow-hidden rounded-sm", barClassName)}>
        {/* Each row's bar always fills its track; the rating segments divide
            that track proportionally to that row's own count. Volume
            comparison between rows is read off the count column on the
            right, not the bar length — otherwise short bars look like they
            contain "No rating" gray space. */}
        <div className="flex h-full w-full">
          {RATING_ORDER.map((bucket) => {
            const value = row.byRating[bucket];
            if (!value) {
              return null;
            }
            return (
              <div
                key={bucket}
                className={cn("h-full", RATING_META[bucket].bar)}
                style={{ width: `${(value / row.total) * 100}%` }}
                title={`${RATING_META[bucket].label}: ${value}`}
              />
            );
          })}
        </div>
      </div>
      <span className={cn("text-muted text-right font-mono text-xs tabular-nums", totalClassName)}>
        {row.total}
      </span>
    </div>
  );
}

function emptyByRating(): Record<RatingBucket, number> {
  return { "0": 0, "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
}

function toBucket(rating: number | null | undefined): RatingBucket {
  return rating == null ? "0" : (String(rating) as RatingBucket);
}

function overallRow(distribution: FeedbackDistributionBucket[]): {
  total: number;
  byRating: Record<RatingBucket, number>;
} {
  const byRating = emptyByRating();
  let total = 0;
  for (const bucket of distribution) {
    byRating[toBucket(bucket.rating)] += bucket.count;
    total += bucket.count;
  }
  return { total, byRating };
}

function categoryRows(distribution: FeedbackDistributionBucket[]): RowCount[] {
  // Seed with the canonical category order so the chart keeps a stable row
  // order regardless of which categories happen to have entries.
  const rows = new Map<string, RowCount>(
    FEEDBACK_CATEGORIES.map((c) => [
      c.value,
      { category: c.value, total: 0, byRating: emptyByRating() },
    ]),
  );

  for (const bucket of distribution) {
    let row = rows.get(bucket.category);
    if (!row) {
      row = { category: bucket.category, total: 0, byRating: emptyByRating() };
      rows.set(bucket.category, row);
    }
    row.total += bucket.count;
    row.byRating[toBucket(bucket.rating)] += bucket.count;
  }

  return Array.from(rows.values()).filter((r) => r.total > 0);
}
